import { queryClient } from '@/app/providers/query-client';
import { clearStoryboardBoardCache } from '@/lib/storyboard/board-cache';
import { setAppliedBuildId } from '@/lib/app-version-check';

/** 更新时保留的 localStorage 键（登录态与基础偏好） */
export const APP_UPDATE_PRESERVED_LOCAL_STORAGE_KEYS = [
  'token',
  'settings-store',
  'i18nextLng',
] as const;

async function clearAllCaches(): Promise<void> {
  if (typeof caches === 'undefined') return;
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
}

/**
 * 清理业务侧本地状态后硬刷新，保留 token / 主题与语言偏好。
 *
 * @param targetBuildId 本次要升级到的远端构建号；传入后记录为「已完成清理」，
 *   刷新后的新代码据此判定无需再次提示。维护模式下的被动刷新不传，不写记录。
 */
export async function applyAppUpdateReload(targetBuildId?: string): Promise<void> {
  const preserved: Partial<
    Record<(typeof APP_UPDATE_PRESERVED_LOCAL_STORAGE_KEYS)[number], string | null>
  > = {};

  for (const key of APP_UPDATE_PRESERVED_LOCAL_STORAGE_KEYS) {
    try {
      preserved[key] = window.localStorage.getItem(key);
    } catch {
      preserved[key] = null;
    }
  }

  queryClient.clear();
  clearStoryboardBoardCache();

  try {
    window.localStorage.clear();
  } catch {
    // ignore
  }

  for (const key of APP_UPDATE_PRESERVED_LOCAL_STORAGE_KEYS) {
    const value = preserved[key];
    if (value !== null && value !== undefined) {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // ignore
      }
    }
  }

  try {
    window.sessionStorage.clear();
  } catch {
    // ignore
  }

  // 必须在 reload 前写入，且写的是目标构建号而非当前运行的旧构建号，
  // 否则刷新后的新代码仍判定为「清理未执行」而反复弹窗
  if (targetBuildId) {
    setAppliedBuildId(targetBuildId);
  }

  await clearAllCaches();
  window.location.reload();
}
